/**
 * 접수 대화 — **실제 메신저처럼 주고받는다**(2026-09-16 사용자 지정:
 * 「실제 채팅앱 UI/UX 로 수정해줘」).
 *
 * 여는 질문 하나는 화면이 던지고, 그 답을 보내면 AI 가 무엇이 비었는지 보고
 * **필요한 질문을 한 번에 1~3개** 낸다. 화면은 그것을 큐에 담아 **하나씩**
 * 던지고, 다 받으면 다시 보낸다 — 그때는 무조건 초안이다. 그래서 모델 호출이
 * 티켓당 최대 둘이고, 사람이 기다리는 시간도 그만큼이 상한이다.
 *
 * ## 채팅앱에서 가져온 것 · 안 가져온 것
 *
 * | 가져온 것 | 왜 |
 * |:--|:--|
 * | 좌우 말풍선 + 보내는 쪽 색 반전 | 누가 한 말인지 **읽기 전에** 안다 |
 * | 상대 아바타 | 말풍선이 여럿 이어져도 PM 의 말이라는 것이 붙어 있다 |
 * | 「쓰는 중」 점 셋 | 30초를 기다리는 자리다 — 멈춘 것과 구별돼야 한다 |
 * | 새 말이 오면 바닥으로 | 스크롤을 손으로 내리게 하면 답을 못 본다 |
 * | 입력칸 옆 보내기 | 손이 가는 자리가 글 쓰는 자리 바로 옆이다 |
 *
 * **시각과 읽음 표시는 안 넣었다.** 한 모달 안에서 1~2분에 끝나는 대화라
 * 「언제 보냈나」가 뜻이 없고, 읽음은 상대가 사람일 때의 개념이다.
 *
 * ⚠ **늦게 온 답이 사람의 다음 말을 덮지 않게** 요청마다 번호를 매기고
 * 마지막 번호의 답만 받는다.
 *
 * **초안을 여기서 저장하지 않는다.** 폼으로 넘기고 사람이 「만들기」를
 * 누른다(2026-09-02 확정 「초안은 티켓이 아니다」).
 */

import { Fragment, useEffect, useRef, useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';

import {
  draftTicket,
  errorMessage,
  NotLoggedInError,
  type DraftAnswer,
  type DraftQuestion,
  type DraftReply,
  type TicketQuestion,
  type TicketKind,
} from '../api';
import { kindLabel } from '../columns';
import { ModalActions } from '../ui/ModalActions';
import type { DraftSeed } from './CreateForm';

/** 보내기 아이콘. 글자 「보내기」는 `aria-label` 로 남는다. */
function SendMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M2 8 14 2.5 8.5 14 7 9.5 2 8Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** PM 아바타. 사람 쪽에는 안 붙인다 — 오른쪽에 선 것이 곧 나다. */
function PmMark() {
  return (
    <span
      aria-hidden="true"
      className="mt-s1 flex h-s6 w-s6 shrink-0 items-center justify-center rounded-full bg-brand-t text-micro font-bold text-brand-d"
    >
      PM
    </span>
  );
}

function FromPm({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-s2">
      <PmMark />
      {/* 말풍선은 칸을 다 안 쓴다 — 한 줄이 너무 길면 눈이 다음 줄을 못 찾는다.
          보내는 쪽과 받는 쪽이 같은 폭이면 좌우 구분도 흐려진다. */}
      <span className="max-w-[78%] rounded-lg rounded-tl-sm bg-sunk px-s3 py-s2 text-body text-ink whitespace-pre-wrap break-keep">
        {children}
      </span>
    </li>
  );
}

function FromMe({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex justify-end">
      <span className="max-w-[78%] rounded-lg rounded-tr-sm bg-primary px-s3 py-s2 text-body text-primary-foreground whitespace-pre-wrap break-keep">
        {children}
      </span>
    </li>
  );
}

export function CreateChat({
  projectId,
  projectName,
  kind,
  questions,
  onDraft,
  onBack,
  onSwitchToForm,
  onCancel,
  onUnauthorized,
}: {
  projectId: number;
  projectName: string;
  /** 사람이 고른 유형. **여기서 안 바뀐다.** */
  kind: TicketKind;
  /** 여는 질문. 서버가 준다(`GET /tickets/meta`) — 화면이 지어내지 않는다. */
  questions: TicketQuestion[];
  onDraft: (seed: DraftSeed, evidence: string[], model: string) => void;
  /** 「뒤로」 — 프로젝트·유형을 다시 고른다. */
  onBack: () => void;
  onSwitchToForm: () => void;
  onCancel: () => void;
  onUnauthorized: () => void;
}) {
  /** 아직 안 던진 물음들. 처음에는 여는 질문, 그 뒤에는 AI 가 낸 것. */
  const [queue, setQueue] = useState<DraftQuestion[]>(() =>
    questions.map((q) => ({ heading: q.heading, text: q.text, options: [] })),
  );
  const [answers, setAnswers] = useState<DraftAnswer[]>([]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);
  const logRef = useRef<HTMLOListElement>(null);

  const current = queue[0] ?? null;

  /* 새 말이 붙으면 바닥으로. **jsdom 에는 `scrollTo` 가 없다** — 없으면 건너뛴다. */
  useEffect(() => {
    const log = logRef.current;
    log?.scrollTo?.({ top: log.scrollHeight, behavior: 'smooth' });
  }, [answers.length, current, pending]);

  async function callServer(next: DraftAnswer[]) {
    const mine = ++seq.current;
    setError(null);
    setPending(true);
    let reply: DraftReply;
    try {
      reply = await draftTicket({ project_id: projectId, kind, answers: next });
    } catch (err) {
      if (mine !== seq.current) return;
      setPending(false);
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      setError(errorMessage(err));
      return;
    }
    if (mine !== seq.current) return;
    setPending(false);
    if (reply.status === 'ask' && reply.questions.length > 0) {
      // **한 번에 온 것을 큐에 담는다.** 답하는 동안 묶음을 안 바꾼다.
      setQueue(reply.questions);
      return;
    }
    if (reply.draft) onDraft(reply.draft, reply.evidence, reply.model);
  }

  /** 지금 떠 있는 물음에 답한다. 큐가 비면 그때 서버를 부른다. */
  function answer(text: string) {
    if (pending || !current) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    setInput('');
    const next = [
      ...answers,
      { heading: current.heading, question: current.text, text: trimmed },
    ];
    const rest = queue.slice(1);
    setAnswers(next);
    setQueue(rest);
    if (rest.length === 0) void callServer(next);
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    answer(input);
  }

  return (
    <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
      {/*
        머리 — 무엇을 고르고 들어왔는지. 대화가 길어져도 안 밀린다.

        ⚠ **「뒤로」는 왼쪽이다.** 오른쪽 위는 껍데기의 닫기(×)가 쓰는 자리라
        (`components/ui/dialog.tsx` 의 `absolute top-s4 right-s4`) 거기 두면
        두 단추가 겹쳐 보인다 — 실제로 겹쳤다. 채팅앱의 뒤로도 왼쪽이다.
      */}
      <header className="flex shrink-0 items-center gap-s2 border-b border-line px-s4 py-s3 pr-s8">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={pending}
          onClick={onBack}
        >
          뒤로
        </Button>
        <h2 className="m-0 text-lead font-bold text-ink">새 티켓</h2>
        <span className="min-w-0 truncate text-meta text-ink-3">
          {projectName} · {kindLabel(kind)}
        </span>
      </header>

      {/* 주고받은 말. **가운데만 스크롤된다** — 머리와 입력칸은 제자리에 있다. */}
      <ol
        ref={logRef}
        role="log"
        aria-label="주고받은 말"
        aria-live="polite"
        className="m-0 flex min-h-0 flex-1 list-none flex-col gap-s3 overflow-y-auto bg-canvas px-s4 py-s4"
      >
        {answers.map((turn, i) => (
          <Fragment key={i}>
            <FromPm>{turn.question}</FromPm>
            <FromMe>{turn.text}</FromMe>
          </Fragment>
        ))}
        {current && !pending && <FromPm>{current.text}</FromPm>}
        {pending && (
          <li className="flex items-start gap-s2" role="status" aria-label="쓰는 중">
            <PmMark />
            <span className="rounded-lg rounded-tl-sm bg-sunk px-s3 py-s3">
              <span className="chat-typing">
                <i />
                <i />
                <i />
              </span>
            </span>
          </li>
        )}
      </ol>

      {/* 입력 — **바닥에 붙박이다.** 대화가 길어져도 쓰는 자리가 안 움직인다. */}
      <div className="shrink-0 border-t border-line px-s4 py-s3">
        {/* 고르게 하면 답이 빨라지는 자리. 채팅앱의 빠른 답장과 같은 꼴이다. */}
        {current && !pending && current.options.length > 0 && (
          <div className="mb-s2 flex flex-wrap gap-s2" role="group" aria-label="선택지">
            {current.options.map((opt) => (
              <Button
                key={opt}
                type="button"
                size="sm"
                variant="outline"
                onClick={() => answer(opt)}
              >
                {opt}
              </Button>
            ))}
          </div>
        )}
        {error && (
          <p role="alert" className="m-0 mb-s2 text-meta text-st-red-f">
            {error}
          </p>
        )}
        <div className="flex items-end gap-s2">
          <textarea
            autoFocus
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              // Enter 로 보낸다. 줄바꿈은 Shift+Enter — 붙여넣은 로그가 여러 줄일 때.
              // ⚠ **`isComposing` 을 봐야 한다** — 한글 조합 중 Enter 가 먼저 나간다.
              if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                answer(input);
              }
            }}
            placeholder="아는 만큼만 적어도 된다"
            aria-label="답"
            rows={2}
            disabled={pending || !current}
            className="min-w-0 flex-1 resize-y rounded-lg border border-line bg-panel px-s3 py-s2 text-body text-ink outline-none placeholder:text-ink-3 focus-visible:border-brand"
          />
          {/* **보내기는 입력칸 옆이다**(채팅앱 꼴). 긍정 단추가 오른쪽 끝이라는
              규칙은 그대로다 — 이 줄에서도 오른쪽 끝이다. */}
          <Button
            type="submit"
            size="icon"
            aria-label="보내기"
            disabled={pending || !current || !input.trim()}
          >
            <SendMark />
          </Button>
        </div>
      </div>

      {/*
        ⚠ **실패에도 길이 있어야 한다**(실측). 마지막 답을 보낸 뒤 실패하면
        물음이 없어 보내기가 잠기는데, 그 자리에서 할 수 있는 것이
        「취소」뿐이면 **적은 답을 통째로 잃는다.**
      */}
      <ModalActions
        placement="frame"
        secondary={[
          ...(error
            ? [
                {
                  label: '다시 보내기',
                  disabled: pending,
                  onClick: () => void callServer(answers),
                },
              ]
            : []),
          { label: '직접 적기', onClick: onSwitchToForm },
        ]}
        cancel={{ label: '취소', onClick: onCancel }}
      />
    </form>
  );
}
